import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { logger } from '../lib/logger';
import { sortByName } from '../lib/sortByName';
import { toast } from 'sonner';
import {
    Shield,
    Star,
    AlertTriangle,
    TrendingDown,
    BarChart3,
    Trash2,
    CheckCircle2,
    Clock,
    Search,
    Filter,
    ChevronDown,
    ChevronLeft,
    Archive,
    RotateCcw,
    Loader2,
    Calendar,
    MessageSquareText,
    Eye
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Sheet,
    SheetContent,
} from '../components/ui/sheet';
import { RecoveryConversationPanel } from '../components/recovery/RecoveryConversationPanel';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import ReviewShieldAnalytics from '../components/ReviewShieldAnalytics';
import { ExperienceBadge } from '../components/shared/ExperienceBadge';
import { isMedicalVertical, scopedOrgVertical } from '../lib/vertical';
import { StatusChip, SLAChip } from '../components/shared/ReviewShieldChips';
import WeeklyDigestSettings from '../components/WeeklyDigestSettings';
import HighlightedText from '../components/shared/HighlightedText';
import { parseUtc } from '../lib/datetime';
import { format } from 'date-fns';

const ReviewShieldResults = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, isSuperAdmin, isClientAdmin } = useAuth();
    const [feedback, setFeedback] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    // v4.34.5 — Debounced mirror of `searchTerm` that actually triggers the
    // backend fetch. Decouples typing speed from request volume so a fast
    // typer doesn't generate a request per keystroke.
    const [effectiveSearch, setEffectiveSearch] = useState('');
    useEffect(() => {
        const handle = setTimeout(() => setEffectiveSearch(searchTerm.trim()), 250);
        return () => clearTimeout(handle);
    }, [searchTerm]);
    // Deep links are the contract: every filter lives in the URL, so a link
    // from the Command Center (or a bookmark) reopens the exact same list.
    const initialParams = new URLSearchParams(window.location.search);
    const [filterStatus, setFilterStatus] = useState(initialParams.get('status') || 'all');
    const [filterRating, setFilterRating] = useState('all');
    const [filterLocation, setFilterLocation] = useState(initialParams.get('location_id') || 'all');
    // "Show cleared" view: cleared items are ARCHIVED, never deleted.
    const [showCleared, setShowCleared] = useState(false);
    const [clearDialogOpen, setClearDialogOpen] = useState(false);
    const [clearPreview, setClearPreview] = useState(null);
    const [clearing, setClearing] = useState(false);
    const [autoClearDays, setAutoClearDays] = useState(null);
    const cameFromCommandCenter = initialParams.get('from') === 'cc';
    const returnTo = initialParams.get('return_to') || '/command-center';
    const [selectedIds, setSelectedIds] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null); // null for bulk, id for single
    const [cleanupDialogOpen, setCleanupDialogOpen] = useState(false);
    const [cleanupDays, setCleanupDays] = useState('30');
    const [deleting, setDeleting] = useState(false);
    const [organizations, setOrganizations] = useState([]);
    const [filterOrganization, setFilterOrganization] = useState(initialParams.get('organization_id') || 'all');
    // v4.38.0 — Experience-type filter propagates through results,
    // analytics, charts, exports, and print. "all" → no filter.
    const [filterExperience, setFilterExperience] = useState('all');
    // MEDICAL ONLY — a clinic has no dine-in / take-out / delivery, so the
    // experience filter and the Experience Type column are hidden and the
    // subtitle says "patient". Every other vertical is untouched: the stored
    // `experience_type` value never changes.
    const isMedical = isMedicalVertical(scopedOrgVertical({
        user, organizations,
        organizationId: filterOrganization !== 'all' ? filterOrganization : null,
    }));
    const [recoveryStatus, setRecoveryStatus] = useState({});
    const [initiatingRecovery, setInitiatingRecovery] = useState(null);
    const [activeConversationId, setActiveConversationId] = useState(null);

    // v4.40.1 — Message viewer. When the operator clicks the compact
    // "💬 Yes" chip we open a lightweight modal showing the full
    // comment; keeps the table density low without hiding data.
    const [messageViewer, setMessageViewer] = useState(null);

    // v4.45 — Email viewer + voucher context. Opens when the operator
    // clicks the "🟢 Yes" chip on the Email column. Fetches the
    // associated voucher (when the row carries a voucher_code) so the
    // administrator gets one-tap access to Copy / Compose / View
    // Voucher without leaving the Operational Base.
    const [emailViewer, setEmailViewer] = useState(null);
    const [emailViewerVoucher, setEmailViewerVoucher] = useState(null);
    const [emailViewerLoading, setEmailViewerLoading] = useState(false);
    const [emailCopied, setEmailCopied] = useState(false);

    // v4.40.1 — Deep-link target: when Timeline (or any caller) sends
    // ?feedback_id=…&experience=…, we preselect the experience filter
    // (so the row is in scope) and scroll-into-view + briefly highlight
    // the target row once it's rendered.
    const [searchParams, setSearchParams] = useSearchParams();
    const deepLinkFeedbackId = searchParams.get('feedback_id');
    const deepLinkExperience = searchParams.get('experience');

    useEffect(() => {
        if (deepLinkExperience && ['dine_in', 'pickup', 'delivery', 'all'].includes(deepLinkExperience)) {
            setFilterExperience(deepLinkExperience);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deepLinkExperience]);

    useEffect(() => {
        if (!deepLinkFeedbackId || loading) return;
        // Wait one tick so the table is in the DOM.
        const handle = setTimeout(() => {
            const el = document.querySelector(`[data-testid="review-shield-row-${deepLinkFeedbackId}"]`);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el.classList.add('rs-deeplink-highlight');
                setTimeout(() => el.classList.remove('rs-deeplink-highlight'), 2400);
            }
            // Strip the deep-link params so a refresh doesn't keep
            // re-highlighting the row.
            const next = new URLSearchParams(searchParams);
            next.delete('feedback_id');
            next.delete('experience');
            setSearchParams(next, { replace: true });
        }, 150);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deepLinkFeedbackId, loading, feedback.length]);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterOrganization, filterLocation, filterStatus, filterExperience, effectiveSearch, showCleared]);

    // Keep the URL in step with the filters so the page is always shareable.
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        const put = (key, value) => {
            if (value && value !== 'all') next.set(key, value); else next.delete(key);
        };
        put('organization_id', filterOrganization);
        put('location_id', filterLocation);
        put('status', filterStatus);
        if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterOrganization, filterLocation, filterStatus]);

    // Arriving from an alert: start at the top of the list, not mid-page.
    useEffect(() => {
        if (cameFromCommandCenter) window.scrollTo({ top: 0, behavior: 'auto' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The location's own auto-clear setting, shown read-only next to Clear all.
    useEffect(() => {
        if (filterLocation === 'all') { setAutoClearDays(null); return; }
        api.get(`/locations/${filterLocation}`)
            .then((r) => setAutoClearDays(r.data?.auto_clear_recovery_days ?? 0))
            .catch(() => setAutoClearDays(null));
    }, [filterLocation]);

    const loadData = async () => {
        try {
            setLoading(true);
            const params = {};
            if (filterOrganization !== 'all') params.organization_id = filterOrganization;
            if (filterLocation !== 'all') params.location_id = filterLocation;
            if (filterStatus !== 'all') params.status = filterStatus;
            if (showCleared) params.cleared = true;
            // v4.38.0 — propagate experience filter to BOTH list + summary
            // so the breakdown chips reflect the active scope.
            if (filterExperience !== 'all') params.experience_type = filterExperience;
            if (effectiveSearch) params.search = effectiveSearch;

            const [feedbackRes, summaryRes] = await Promise.all([
                api.get('/review-shield/feedback', { params }),
                api.get('/review-shield/feedback/summary', { params })
            ]);
            setFeedback(feedbackRes.data);
            setSummary(summaryRes.data);

            // Fetch recovery session status for feedback items with emails
            if (feedbackRes.data.length > 0) {
                try {
                    const ids = feedbackRes.data.map(f => f.id);
                    const statusRes = await api.post('/recovery/feedback-status', { feedback_ids: ids });
                    setRecoveryStatus(statusRes.data);
                } catch (e) {
                    console.error('Failed to load recovery status:', e);
                }
            }

            // Load orgs for super admin filter
            if (isSuperAdmin()) {
                try {
                    const orgsRes = await api.get('/organizations');
                    setOrganizations(sortByName(orgsRes.data));
                } catch (orgErr) {
                    logger.warn('[ReviewShieldResults] org list load failed', orgErr);
                }
            }
        } catch (err) {
            console.error('Failed to load Review Shield data:', err);
            toast.error('Failed to load Review Shield data');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusToggle = async (id, currentStatus) => {
        const newStatus = currentStatus === 'open' ? 'resolved' : 'open';
        try {
            await api.put(`/review-shield/feedback/${id}/status`, { status: newStatus });
            setFeedback(prev => prev.map(f => f.id === id ? { ...f, status: newStatus } : f));
            toast.success(`Status updated to ${newStatus}`);
        } catch (err) {
            toast.error('Failed to update status');
        }
    };

    const handleDeleteSingle = (id) => {
        setDeleteTarget(id);
        setDeleteDialogOpen(true);
    };

    const handleDeleteBulk = () => {
        if (selectedIds.length === 0) {
            toast.error('No records selected');
            return;
        }
        setDeleteTarget(null);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        setDeleting(true);
        try {
            if (deleteTarget) {
                await api.delete(`/review-shield/feedback/${deleteTarget}`);
                setFeedback(prev => prev.filter(f => f.id !== deleteTarget));
                toast.success('Feedback deleted');
            } else {
                await api.post('/review-shield/feedback/bulk-delete', { feedback_ids: selectedIds });
                setFeedback(prev => prev.filter(f => !selectedIds.includes(f.id)));
                setSelectedIds([]);
                toast.success(`Deleted ${selectedIds.length} records`);
            }
            // Refresh summary
            const params = {};
            if (filterOrganization !== 'all') params.organization_id = filterOrganization;
            if (filterExperience !== 'all') params.experience_type = filterExperience;
            const summaryRes = await api.get('/review-shield/feedback/summary', { params });
            setSummary(summaryRes.data);
        } catch (err) {
            toast.error('Failed to delete');
        } finally {
            setDeleting(false);
            setDeleteDialogOpen(false);
            setDeleteTarget(null);
        }
    };

    const handleCleanup = async () => {
        setDeleting(true);
        try {
            const days = parseInt(cleanupDays);
            if (isNaN(days) || days < 1) {
                toast.error('Invalid number of days');
                return;
            }
            const res = await api.post('/review-shield/feedback/cleanup', { days });
            toast.success(`Deleted ${res.data.deleted_count} records older than ${days} days`);
            setCleanupDialogOpen(false);
            loadData();
        } catch (err) {
            toast.error('Failed to cleanup records');
        } finally {
            setDeleting(false);
        }
    };

    const toggleSelectAll = () => {
        if (selectedIds.length === filteredFeedback.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(filteredFeedback.map(f => f.id));
        }
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const handleInitiateRecovery = async (feedbackId) => {
        setInitiatingRecovery(feedbackId);
        try {
            const res = await api.post('/recovery/initiate', { feedback_id: feedbackId });
            toast.success('Recovery session initiated — secure link sent to customer');
            // Update status for this feedback
            setRecoveryStatus(prev => ({
                ...prev,
                [feedbackId]: {
                    ...prev[feedbackId],
                    active_session: { session_id: res.data.session_id, status: 'active' }
                }
            }));
        } catch (err) {
            const detail = err.response?.data?.detail || 'Failed to initiate recovery session';
            if (err.response?.status === 409) {
                toast.info('A recovery session already exists for this feedback');
                // Refresh statuses
                try {
                    const ids = feedback.map(f => f.id);
                    const statusRes = await api.post('/recovery/feedback-status', { feedback_ids: ids });
                    setRecoveryStatus(statusRes.data);
                } catch {}
            } else {
                toast.error(detail);
            }
        } finally {
            setInitiatingRecovery(null);
        }
    };

    const handleViewConversation = (sessionId) => {
        setActiveConversationId(sessionId);
    };

    const handleConversationClose = () => {
        setActiveConversationId(null);
    };

    const handleConversationStatusChange = async () => {
        // Refresh recovery statuses when a session is resolved
        try {
            const ids = feedback.map(f => f.id);
            const statusRes = await api.post('/recovery/feedback-status', { feedback_ids: ids });
            setRecoveryStatus(statusRes.data);
        } catch (err) {
            logger.warn('[ReviewShieldResults] recovery status refresh failed', err);
        }
    };

    // v4.45 — Email Details modal. Opens the row's captured email
    // (from either the feedback row or the associated LR voucher) and
    // — if a voucher was issued — enriches the modal with the voucher
    // configuration name, code, status, and dates. Reuses the existing
    // /vouchers/lookup-by-code endpoint; no per-row prefetch.
    const openEmailViewer = async (item) => {
        setEmailViewer(item);
        setEmailViewerVoucher(null);
        setEmailCopied(false);
        if (item?.voucher_code) {
            setEmailViewerLoading(true);
            try {
                const res = await api.get(`/vouchers/lookup-by-code/${item.voucher_code}`);
                setEmailViewerVoucher(res.data);
            } catch (err) {
                logger.warn('[ReviewShieldResults] voucher lookup failed', err);
            } finally {
                setEmailViewerLoading(false);
            }
        }
    };

    const closeEmailViewer = () => {
        setEmailViewer(null);
        setEmailViewerVoucher(null);
        setEmailCopied(false);
    };

    const copyEmailToClipboard = async (email) => {
        try {
            await navigator.clipboard.writeText(email);
            setEmailCopied(true);
            setTimeout(() => setEmailCopied(false), 2000);
        } catch (_) {
            // Fallback for older browsers
            const ta = document.createElement('textarea');
            ta.value = email;
            document.body.appendChild(ta);
            ta.select();
            try { document.execCommand('copy'); } catch (_) { /* ignore */ }
            document.body.removeChild(ta);
            setEmailCopied(true);
            setTimeout(() => setEmailCopied(false), 2000);
        }
    };

    // Locations the visible rows belong to, by ID (names repeat across
    // organizations), A→Z in the current language.
    const locations = sortByName(
        Object.values((feedback || []).reduce((acc, f) => {
            if (f.location_id && !acc[f.location_id]) {
                acc[f.location_id] = { id: f.location_id, name: f.location_name || f.location_id };
            }
            return acc;
        }, {}))
    );

    const clearScopeBody = () => {
        const place = filterLocation !== 'all'
            ? (locations.find((l) => l.id === filterLocation)?.name || '')
            : (filterOrganization !== 'all'
                ? (organizations.find((o) => o.id === filterOrganization)?.name || '')
                : '');
        const count = clearPreview ?? 0;
        return place
            ? t('reviewShield.clearAll.bodyPlace', { count, place })
            : t('reviewShield.clearAll.bodyEverywhere', { count });
    };

    const clearScopeParams = () => {
        const p = {};
        if (filterOrganization !== 'all') p.organization_id = filterOrganization;
        if (filterLocation !== 'all') p.location_id = filterLocation;
        return p;
    };

    const openClearDialog = async () => {
        try {
            const res = await api.get('/review-shield/feedback/clear-preview', { params: clearScopeParams() });
            const count = res.data?.count || 0;
            if (!count) { toast.info(t('reviewShield.clearAll.none')); return; }
            setClearPreview(count);
            setClearDialogOpen(true);
        } catch {
            toast.error(t('reviewShield.clearAll.failed'));
        }
    };

    const undoClear = async (batchId) => {
        try {
            const res = await api.post('/review-shield/feedback/clear-undo',
                { batch_id: batchId, ...clearScopeParams() });
            toast.success(t('reviewShield.clearAll.undone', { count: res.data?.restored || 0 }));
            loadData();
        } catch {
            toast.error(t('reviewShield.clearAll.failed'));
        }
    };

    const doClearAll = async () => {
        setClearing(true);
        try {
            const res = await api.post('/review-shield/feedback/clear-open', clearScopeParams());
            const cleared = res.data?.cleared || 0;
            const batchId = res.data?.batch_id;
            setClearDialogOpen(false);
            toast.success(t('reviewShield.clearAll.done', { count: cleared }), {
                duration: 60000,
                action: batchId ? { label: t('reviewShield.clearAll.undo'), onClick: () => undoClear(batchId) } : undefined,
            });
            loadData();
        } catch {
            toast.error(t('reviewShield.clearAll.failed'));
        } finally {
            setClearing(false);
        }
    };

    const restoreOne = async (id) => {
        try {
            const res = await api.post('/review-shield/feedback/clear-undo',
                { feedback_ids: [id], ...clearScopeParams() });
            toast.success(t('reviewShield.cleared.restored', { count: res.data?.restored || 0 }));
            loadData();
        } catch {
            toast.error(t('reviewShield.clearAll.failed'));
        }
    };

    const canClear = ['super_admin', 'client_admin', 'location_manager'].includes(user?.role);

    const filteredFeedback = feedback.filter(f => {
        if (filterStatus !== 'all' && f.status !== filterStatus) return false;
        if (filterRating !== 'all') {
            if (filterRating === 'low' && f.rating > 3) return false;
            if (filterRating === 'high' && f.rating < 4) return false;
        }
        if (filterLocation !== 'all' && f.location_id !== filterLocation) return false;
        // v4.34.5 — Server-side search via `effectiveSearch`; the local list
        // already contains only matching rows. Status/Rating/Location remain
        // client-side because they're constant-time lookups on small enum-like
        // values and the user toggles them frequently.
        return true;
    });

    const formatDate = (ts) => {
        if (!ts) return '-';
        // Platform-standard event timestamp (same as Analytics → Recent Taps).
        return format(parseUtc(ts), 'MMM dd, HH:mm:ss');
    };

    const renderStars = (count) => (
        <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map(s => (
                <Star
                    key={s}
                    className="w-3.5 h-3.5"
                    fill={s <= count ? '#FBBF24' : 'transparent'}
                    stroke={s <= count ? '#FBBF24' : '#94a3b8'}
                    strokeWidth={1.5}
                />
            ))}
        </div>
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="review-shield-results">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-foreground flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <Shield className="w-6 h-6 text-emerald-500" />
                        {t('reviewShield.title', 'Review Shield Results')}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {isMedical
                            ? t('reviewShield.subtitleMedical', 'Patient feedback collected through Review Shield')
                            : t('reviewShield.subtitle', 'Guest feedback collected through Review Shield')}
                    </p>
                </div>
                <div className="flex gap-2">
                    {selectedIds.length > 0 && (
                        <Button
                            variant="destructive"
                            size="sm"
                            onClick={handleDeleteBulk}
                            data-testid="review-shield-bulk-delete-btn"
                        >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete ({selectedIds.length})
                        </Button>
                    )}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" data-testid="review-shield-cleanup-btn">
                                <Calendar className="w-4 h-4 mr-1" />
                                Cleanup
                                <ChevronDown className="w-3 h-3 ml-1" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => { setCleanupDays('30'); setCleanupDialogOpen(true); }}>
                                Older than 30 days
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { setCleanupDays('60'); setCleanupDialogOpen(true); }}>
                                Older than 60 days
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { setCleanupDays('90'); setCleanupDialogOpen(true); }}>
                                Older than 90 days
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { setCleanupDays(''); setCleanupDialogOpen(true); }}>
                                Custom...
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            {/* Summary Metrics */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                                <BarChart3 className="w-4 h-4" />
                                {t('reviewShield.totalFeedback', 'Total Feedback')}
                            </div>
                            <div className="text-2xl font-bold text-foreground" data-testid="review-shield-total">
                                {summary.total_feedback}
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                                <Star className="w-4 h-4 text-amber-500" />
                                {t('reviewShield.avgScore', 'Avg. Score')}
                            </div>
                            <div className="text-2xl font-bold text-foreground" data-testid="review-shield-avg-score">
                                {summary.average_score}
                                <span className="text-sm font-normal text-muted-foreground ml-1">/5</span>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                                <AlertTriangle className="w-4 h-4 text-red-500" />
                                {t('reviewShield.lowRatings', 'Low Ratings')}
                            </div>
                            <div className="text-2xl font-bold text-red-600" data-testid="review-shield-low-ratings">
                                {summary.low_ratings_count}
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                                <TrendingDown className="w-4 h-4 text-orange-500" />
                                {t('reviewShield.topCategory', 'Top Category')}
                            </div>
                            <div className="text-2xl font-bold text-foreground" data-testid="review-shield-top-category">
                                {summary.top_category
                                    ? t(`reviewShield.improvementCategories.${summary.top_category}`, summary.top_category)
                                    : '-'}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Analytics Charts */}
            <ReviewShieldAnalytics
                organizationId={filterOrganization}
                locationId={filterLocation}
                experienceType={filterExperience}
            />

            {/* Weekly Digest Settings */}
            {(() => {
                // HARD RULE: Never default to organizations[0]
                // When "All Organizations" is selected, derive org from visible feedback or require selection
                let digestOrgId = null;
                let digestOrgName = '';
                let multiOrgWarning = false;

                if (isSuperAdmin()) {
                    if (filterOrganization !== 'all') {
                        // Explicit org selected - use it
                        digestOrgId = filterOrganization;
                        digestOrgName = organizations.find(o => o.id === filterOrganization)?.name || '';
                    } else {
                        // "All Organizations" selected - detect from visible feedback
                        const orgIdsInFeedback = [...new Set(feedback.map(f => f.organization_id).filter(Boolean))];
                        if (orgIdsInFeedback.length === 1) {
                            // Single org in visible data - use it
                            digestOrgId = orgIdsInFeedback[0];
                            digestOrgName = organizations.find(o => o.id === digestOrgId)?.name || 
                                feedback.find(f => f.organization_id === digestOrgId)?.organization_name || '';
                        } else if (orgIdsInFeedback.length > 1) {
                            // Multiple orgs - require explicit selection
                            multiOrgWarning = true;
                        }
                        // If no feedback at all, digestOrgId stays null - widget hidden
                    }
                } else {
                    digestOrgId = user?.organization_id;
                }

                if (multiOrgWarning) {
                    return (
                        <Card data-testid="weekly-digest-multi-org-warning">
                            <CardContent className="pt-5">
                                <div className="flex items-center gap-2 mb-2">
                                    <Shield className="w-5 h-5 text-amber-500" />
                                    <h3 className="text-base font-semibold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                        {t('weeklyDigest.title', 'Weekly Digest')}
                                    </h3>
                                </div>
                                <p className="text-sm text-amber-600 dark:text-amber-400">
                                    {t('weeklyDigest.multiOrgWarning', 'Multiple organizations detected. Please select one organization to configure and preview the digest.')}
                                </p>
                            </CardContent>
                        </Card>
                    );
                }

                return digestOrgId ? (
                    <WeeklyDigestSettings
                        organizationId={digestOrgId}
                        organizationName={digestOrgName}
                    />
                ) : null;
            })()}

            {/* Filters */}
            <div className="flex flex-wrap gap-3" data-testid="review-shield-filters">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder={t('reviewShield.searchPlaceholder', 'Search messages, locations, categories...')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9 pr-9"
                        data-testid="review-shield-search"
                    />
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted text-muted-foreground"
                            data-testid="review-shield-search-clear"
                            aria-label={t('common.clear', 'Clear')}
                        >
                            <span className="text-xs">✕</span>
                        </button>
                    )}
                </div>
                {isSuperAdmin() && organizations.length > 0 && (
                    <Select value={filterOrganization} onValueChange={setFilterOrganization}>
                        <SelectTrigger className="w-[180px]" data-testid="review-shield-filter-org">
                            <SelectValue placeholder={t('reviewShieldResults.organization')} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('reviewShield.filters.allOrganizations')}</SelectItem>
                            {organizations.map(org => (
                                <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
                {(locations.length > 1 || filterLocation !== 'all') && (
                    <Select value={filterLocation} onValueChange={setFilterLocation}>
                        <SelectTrigger className="w-[160px]" data-testid="review-shield-filter-location">
                            <SelectValue placeholder={t('reviewShieldResults.location')} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('reviewShield.filters.allLocations')}</SelectItem>
                            {locations.map(loc => (
                                <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-[130px]" data-testid="review-shield-filter-status">
                        <SelectValue placeholder={t('reviewShieldResults.status')} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('reviewShield.filters.allStatus')}</SelectItem>
                        <SelectItem value="open">{t('reviewShield.filters.statusOpen')}</SelectItem>
                        <SelectItem value="resolved">{t('reviewShield.filters.statusResolved')}</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={filterRating} onValueChange={setFilterRating}>
                    <SelectTrigger className="w-[130px]" data-testid="review-shield-filter-rating">
                        <SelectValue placeholder={t('reviewShieldResults.rating')} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('reviewShield.filters.allRatings')}</SelectItem>
                        <SelectItem value="low">Low (1-3)</SelectItem>
                        <SelectItem value="high">High (4-5)</SelectItem>
                    </SelectContent>
                </Select>
                {/* v4.38.0 — Experience-type filter. Drives results, analytics,
                  * trends, AI insights cards, exports, print, and follow-up
                  * queue scope. "all" = no scope. */}
                {!isMedical && (
                <Select value={filterExperience} onValueChange={setFilterExperience}>
                    <SelectTrigger className="w-[180px]" data-testid="review-shield-filter-experience" data-insight-id="review-shield-filter-experience">
                        <SelectValue placeholder={t('reviewShield.experience.label')} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all" data-testid="review-shield-filter-experience-all">{t('reviewShield.experience.all')}</SelectItem>
                        <SelectItem value="dine_in" data-testid="review-shield-filter-experience-dine_in">
                            {t('reviewShield.experience.dine_in_emoji')} {t('reviewShield.experience.dine_in')}
                        </SelectItem>
                        <SelectItem value="pickup" data-testid="review-shield-filter-experience-pickup">
                            {t('reviewShield.experience.pickup_emoji')} {t('reviewShield.experience.pickup')}
                        </SelectItem>
                        <SelectItem value="delivery" data-testid="review-shield-filter-experience-delivery">
                            {t('reviewShield.experience.delivery_emoji')} {t('reviewShield.experience.delivery')}
                        </SelectItem>
                    </SelectContent>
                </Select>
                )}
            </div>

            {/* Scope bar: where you came from, what you can do with this scope.
                Clearing ARCHIVES — the reviews, ratings and reports keep every
                item, so this is never a delete. */}
            <div className="flex flex-wrap items-center gap-3" data-testid="review-shield-scope-bar">
                {cameFromCommandCenter && (
                    <Button variant="outline" size="sm" className="gap-1" data-testid="rs-back-to-cc"
                        onClick={() => navigate(returnTo)}>
                        <ChevronLeft className="w-4 h-4" />
                        {t('reviewShield.backToCommandCenter')}
                    </Button>
                )}
                {canClear && !showCleared && (
                    <Button variant="outline" size="sm" className="gap-1" data-testid="rs-clear-all"
                        onClick={openClearDialog}>
                        <Archive className="w-4 h-4" />
                        {t('reviewShield.clearAll.button')}
                    </Button>
                )}
                {canClear && (
                    <Button variant="ghost" size="sm" className="gap-1" data-testid="rs-show-cleared"
                        onClick={() => setShowCleared((v) => !v)}>
                        <RotateCcw className="w-4 h-4" />
                        {showCleared ? t('reviewShield.cleared.showOpen') : t('reviewShield.cleared.show')}
                    </Button>
                )}
                {canClear && autoClearDays !== null && (
                    <span className="text-xs text-muted-foreground" data-testid="rs-auto-clear-note">
                        {t('reviewShield.autoClear.inline', {
                            value: autoClearDays
                                ? t('reviewShield.autoClear.days', { count: autoClearDays })
                                : t('reviewShield.autoClear.off'),
                        })}
                    </span>
                )}
            </div>

            <Dialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
                <DialogContent data-testid="rs-clear-dialog">
                    <DialogHeader>
                        <DialogTitle>{t('reviewShield.clearAll.title')}</DialogTitle>
                        <DialogDescription data-testid="rs-clear-dialog-body">
                            {clearScopeBody()}
                        </DialogDescription>
                    </DialogHeader>
                    <p className="text-xs text-muted-foreground">{t('reviewShield.clearAll.note')}</p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setClearDialogOpen(false)} data-testid="rs-clear-cancel">
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button onClick={doClearAll} disabled={clearing} data-testid="rs-clear-confirm">
                            {clearing && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                            {t('reviewShield.clearAll.confirm')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* v4.34.5 — Result counter visible whenever a search is active.
                Makes "search returned 0 results" unambiguous (vs an empty
                page that could be confused with a render bug). */}
            {effectiveSearch && !loading && (
                <p
                    className="text-xs text-muted-foreground"
                    data-testid="review-shield-search-count"
                >
                    {filteredFeedback.length === 0
                        ? t('reviewShield.searchNoResults', 'No feedback matches "{{q}}"', { q: effectiveSearch })
                        : t('reviewShield.searchResultsCount', '{{count}} result(s) for "{{q}}"', { count: filteredFeedback.length, q: effectiveSearch })}
                </p>
            )}

            {/* Table */}
            <Card>
                <CardContent className="p-0">
                    {filteredFeedback.length === 0 ? (
                        <div className="text-center py-12 px-4">
                            <Shield className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                            <h3 className="text-lg font-medium text-foreground mb-1">
                                {t('reviewShield.emptyTitle', 'No feedback yet')}
                            </h3>
                            <p className="text-sm text-muted-foreground max-w-md mx-auto">
                                {t('reviewShield.emptyDescription', 'Feedback will appear here once guests start using Review Shield. Enable Review Shield on your cards to start collecting feedback.')}
                            </p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-10">
                                        <Checkbox
                                            checked={selectedIds.length === filteredFeedback.length && filteredFeedback.length > 0}
                                            onCheckedChange={toggleSelectAll}
                                            data-testid="review-shield-select-all"
                                        />
                                    </TableHead>
                                    <TableHead>{t('reviewShield.date', 'Date')}</TableHead>
                                    <TableHead>{t('reviewShield.locationCol', 'Location')}</TableHead>
                                    <TableHead>{t('reviewShield.cardCol', 'Card')}</TableHead>
                                    <TableHead>{t('reviewShield.employeeCol', 'Employee')}</TableHead>
                                    {!isMedical && <TableHead>{t('reviewShield.experience.label')}</TableHead>}
                                    <TableHead>{t('reviewShield.ratingCol', 'Rating')}</TableHead>
                                    <TableHead>{t('reviewShield.categoryCol', 'Category')}</TableHead>
                                    <TableHead className="min-w-[90px]">{t('reviewShield.messageCol', 'Comment')}</TableHead>
                                    <TableHead>{t('reviewShield.emailCol', 'Email')}</TableHead>
                                    <TableHead>{t('reviewShield.voucherCol', 'Voucher')}</TableHead>
                                    <TableHead>{t('reviewShield.continuedCol', 'Continued')}</TableHead>
                                    <TableHead>{t('reviewShield.statusCol', 'Status')}</TableHead>
                                    <TableHead className="w-40">{t('common.actions', 'Actions')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredFeedback.map((item) => (
                                    <TableRow key={item.id} data-testid={`review-shield-row-${item.id}`}>
                                        <TableCell>
                                            <Checkbox
                                                checked={selectedIds.includes(item.id)}
                                                onCheckedChange={() => toggleSelect(item.id)}
                                            />
                                        </TableCell>
                                        <TableCell className="text-sm whitespace-nowrap">
                                            {formatDate(item.timestamp)}
                                        </TableCell>
                                        <TableCell className="text-sm">
                                            <HighlightedText
                                                text={item.location_name}
                                                query={effectiveSearch}
                                                fallback={<span className="text-muted-foreground">—</span>}
                                            />
                                        </TableCell>
                                        {/* v4.40 — Card: friendly name of the Tap
                                            Engine Card that initiated the
                                            interaction. Falls back to a muted em-
                                            dash when the card is unnamed or the
                                            feedback originated outside a card. */}
                                        <TableCell className="text-sm">
                                            {item.card_friendly_name ? (
                                                <HighlightedText
                                                    text={item.card_friendly_name}
                                                    query={effectiveSearch}
                                                    fallback={<span className="text-muted-foreground">—</span>}
                                                    testId={`review-shield-card-${item.id}`}
                                                />
                                            ) : (
                                                <span className="text-muted-foreground text-xs" data-testid={`review-shield-card-${item.id}`}>—</span>
                                            )}
                                        </TableCell>
                                        {/* v4.40 — Employee: display the assigned
                                            employee (persisted on the feedback
                                            row when Employee Mode is active).
                                            When absent, show the localized
                                            "Unassigned" chip so the operator can
                                            spot gaps. */}
                                        <TableCell className="text-sm">
                                            {item.employee_name ? (
                                                <HighlightedText
                                                    text={item.employee_name}
                                                    query={effectiveSearch}
                                                    fallback={<span className="text-muted-foreground">—</span>}
                                                    testId={`review-shield-employee-${item.id}`}
                                                />
                                            ) : (
                                                <span className="text-muted-foreground text-xs italic" data-testid={`review-shield-employee-${item.id}`}>
                                                    {t('reviewShield.unassigned', 'Unassigned')}
                                                </span>
                                            )}
                                        </TableCell>
                                        {!isMedical && (
                                            <TableCell>
                                                <ExperienceBadge kind={item.experience_type || 'dine_in'} size="xs" />
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            {renderStars(item.rating)}
                                        </TableCell>
                                        <TableCell className="text-sm">
                                            {/* ONE canonical vocabulary, translated. The
                                                raw stored value (survey question text, a
                                                per-experience id, or the hotel "none"
                                                sentinel) is never shown — it stays on the
                                                title attribute for operators who need it. */}
                                            {item.category_canonical ? (
                                                <HighlightedText
                                                    text={t(`reviewShield.improvementCategories.${item.category_canonical}`, item.category_canonical)}
                                                    query={effectiveSearch}
                                                    fallback={<span className="text-muted-foreground">—</span>}
                                                />
                                            ) : (
                                                <span
                                                    className="text-muted-foreground text-xs"
                                                    data-testid={`review-shield-category-${item.id}`}
                                                >
                                                    {t('reviewShield.categoryNone', 'None')}
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-sm">
                                            {/* v4.40.1 — Compact "Comment" chip.
                                                💬 Yes → clickable, opens the
                                                message viewer modal with the
                                                full text (highlighted for the
                                                active search term). No → muted
                                                "No" so an empty comment reads
                                                clearly and takes minimal width. */}
                                            {item.message ? (
                                                <button
                                                    type="button"
                                                    onClick={() => setMessageViewer(item)}
                                                    className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 hover:underline focus:outline-none"
                                                    data-testid={`review-shield-msg-${item.id}`}
                                                    title={item.message}
                                                >
                                                    <MessageSquareText className="w-3.5 h-3.5" />
                                                    <span className="text-xs font-medium">
                                                        {t('reviewShield.commentYes', 'Yes')}
                                                    </span>
                                                </button>
                                            ) : (
                                                <span
                                                    className="text-muted-foreground text-xs"
                                                    data-testid={`review-shield-msg-${item.id}`}
                                                >
                                                    {t('reviewShield.commentNo', 'No')}
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {/* v4.40 — Email: 🟢 Yes if captured
                                                anywhere in the journey (feedback
                                                field OR LR voucher issuance),
                                                ⚪ No otherwise.
                                                v4.45 — Yes chip is CLICKABLE:
                                                opens the Email Details modal
                                                with the address + voucher
                                                context + one-tap actions. */}
                                            {(item.email || item.voucher_status === 'issued') ? (
                                                <button
                                                    type="button"
                                                    onClick={() => openEmailViewer(item)}
                                                    className="focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-emerald-500 rounded-md"
                                                    data-testid={`review-shield-email-${item.id}`}
                                                    title={item.email || ''}
                                                >
                                                    <Badge variant="default" className="text-xs cursor-pointer hover:opacity-80 transition-opacity">
                                                        <span className="mr-1">🟢</span>
                                                        {t('common.yes', 'Yes')}
                                                    </Badge>
                                                </button>
                                            ) : (
                                                <Badge variant="secondary" className="text-xs" data-testid={`review-shield-email-${item.id}`}>
                                                    <span className="mr-1">⚪</span>
                                                    {t('common.no', 'No')}
                                                </Badge>
                                            )}
                                        </TableCell>
                                        {/* v4.40 — Voucher status column.
                                            v4.44 — Card CONFIG (loyal_review_
                                            enabled) is now cleanly separated
                                            from guest DECISION (voucher_
                                            status). Four states:
                                              🟢 Issued   — LR enabled + guest accepted
                                              🟠 Declined — LR enabled + guest chose "Leave a Review"
                                              🔴 Failed   — LR enabled + backend error
                                              ⚪ Off      — LR feature disabled on the card
                                              —           — LR enabled but guest never reached the choice
                                                            (low-rating row or abandoned mid-flow) */}
                                        <TableCell>
                                            {item.voucher_status === 'issued' ? (
                                                <Badge variant="default" className="text-xs" title={item.voucher_code || ''} data-testid={`review-shield-voucher-${item.id}`}>
                                                    <span className="mr-1">🟢</span>
                                                    {t('reviewShield.voucherIssued', 'Issued')}
                                                </Badge>
                                            ) : item.voucher_status === 'declined' ? (
                                                <Badge className="text-xs bg-amber-500 hover:bg-amber-600 text-white" data-testid={`review-shield-voucher-${item.id}`}>
                                                    <span className="mr-1">🟠</span>
                                                    {t('reviewShield.voucherDeclined', 'Declined')}
                                                </Badge>
                                            ) : item.voucher_status === 'failed' ? (
                                                <Badge variant="destructive" className="text-xs" data-testid={`review-shield-voucher-${item.id}`}>
                                                    <span className="mr-1">🔴</span>
                                                    {t('reviewShield.voucherFailed', 'Failed')}
                                                </Badge>
                                            ) : !item.loyal_review_enabled ? (
                                                <Badge variant="secondary" className="text-xs" data-testid={`review-shield-voucher-${item.id}`}>
                                                    <span className="mr-1">⚪</span>
                                                    {t('reviewShield.voucherOff', 'Off')}
                                                </Badge>
                                            ) : (
                                                <span className="text-muted-foreground text-xs" data-testid={`review-shield-voucher-${item.id}`}>—</span>
                                            )}
                                        </TableCell>
                                        {/* v4.40 — Continued column. v4.43 —
                                            NEVER blank: when the card has a
                                            configured public-review platform
                                            we always show it. Green when the
                                            guest clicked Continue (beacon
                                            fired); red when the card is
                                            configured but the guest exited
                                            before clicking. Only shows a
                                            muted em-dash for the edge case
                                            where the card has no configured
                                            platform at all. */}
                                        <TableCell>
                                            {(() => {
                                                const platformKey = item.continued_platform || item.configured_platform;
                                                if (!platformKey) {
                                                    return (
                                                        <span
                                                            className="text-muted-foreground text-xs"
                                                            data-testid={`review-shield-continued-${item.id}`}
                                                        >
                                                            —
                                                        </span>
                                                    );
                                                }
                                                const label = t(
                                                    `reviewShield.platformShort.${platformKey}`,
                                                    (platformKey || '')
                                                        .replace(/_/g, ' ')
                                                        .replace(/\b\w/g, (c) => c.toUpperCase()),
                                                );
                                                if (item.continued_platform) {
                                                    return (
                                                        <Badge
                                                            variant="default"
                                                            className="text-xs"
                                                            title={item.continued_at || ''}
                                                            data-testid={`review-shield-continued-${item.id}`}
                                                        >
                                                            <span className="mr-1">🟢</span>
                                                            {label}
                                                        </Badge>
                                                    );
                                                }
                                                return (
                                                    <Badge
                                                        variant="destructive"
                                                        className="text-xs"
                                                        title={t('reviewShield.continuedNotClicked', 'Guest exited before clicking Continue')}
                                                        data-testid={`review-shield-continued-${item.id}`}
                                                    >
                                                        <span className="mr-1">🔴</span>
                                                        {label}
                                                    </Badge>
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-wrap items-center gap-1.5">
                                                <button
                                                    onClick={() => handleStatusToggle(item.id, item.status)}
                                                    data-testid={`review-shield-status-${item.id}`}
                                                >
                                                    {item.status === 'resolved' ? (
                                                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-200 cursor-pointer text-xs">
                                                            <CheckCircle2 className="w-3 h-3 mr-1" />
                                                            Resolved
                                                        </Badge>
                                                    ) : (
                                                        <Badge variant="outline" className="text-amber-600 border-amber-300 hover:bg-amber-50 cursor-pointer text-xs">
                                                            <Clock className="w-3 h-3 mr-1" />
                                                            Open
                                                        </Badge>
                                                    )}
                                                </button>
                                                {/* v4.39 — presentation-only chips derived from existing fields.
                                                  * StatusChip → ⏳/🟢/⚪ based on (status, contact_requested).
                                                  * SLAChip    → 🟢/🟡/🔴 based on age of timestamp. */}
                                                <StatusChip row={item} />
                                                <SLAChip row={item} />
                                                {item.cleared_at && (
                                                    <>
                                                        <Badge variant="outline" className="text-[10px]"
                                                            title={item.cleared_reason === 'auto'
                                                                ? t('reviewShield.cleared.auto', { when: formatDate(item.cleared_at) })
                                                                : t('reviewShield.cleared.by', { who: item.cleared_by_email || '—', when: formatDate(item.cleared_at) })}
                                                            data-testid={`rs-cleared-badge-${item.id}`}>
                                                            {t('reviewShield.cleared.badge')}
                                                        </Badge>
                                                        {canClear && (
                                                            <Button variant="ghost" size="sm" className="h-6 px-1.5 text-xs"
                                                                data-testid={`rs-restore-${item.id}`}
                                                                onClick={() => restoreOne(item.id)}>
                                                                {t('reviewShield.cleared.restore')}
                                                            </Button>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1">
                                                {(() => {
                                                    const rs = recoveryStatus[item.id];
                                                    if (!rs) return null;
                                                    if (!rs.card_direct_response_enabled) return null;
                                                    if (!rs.has_email) return null;

                                                    if (rs.active_session) {
                                                        return (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleViewConversation(rs.active_session.session_id)}
                                                                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 text-xs h-7 px-2"
                                                                data-testid={`recovery-view-${item.id}`}
                                                            >
                                                                <Eye className="w-3.5 h-3.5 mr-1" />
                                                                View Conversation
                                                            </Button>
                                                        );
                                                    }

                                                    return (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleInitiateRecovery(item.id)}
                                                            disabled={initiatingRecovery === item.id}
                                                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 text-xs h-7 px-2"
                                                            data-testid={`recovery-respond-${item.id}`}
                                                        >
                                                            {initiatingRecovery === item.id ? (
                                                                <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                                                            ) : (
                                                                <MessageSquareText className="w-3.5 h-3.5 mr-1" />
                                                            )}
                                                            Respond
                                                        </Button>
                                                    );
                                                })()}
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleDeleteSingle(item.id)}
                                                    className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 px-1.5"
                                                    data-testid={`review-shield-delete-${item.id}`}
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            <div className="text-sm text-muted-foreground">
                Showing {filteredFeedback.length} of {feedback.length} records
            </div>

            {/* v4.40.1 — Message viewer modal. Opens when the operator
                clicks the compact "💬 Yes" chip on a row. Renders the
                full comment (highlighted for the active search term)
                without leaving the Operational Base. */}
            <Dialog open={!!messageViewer} onOpenChange={(open) => { if (!open) setMessageViewer(null); }}>
                <DialogContent data-testid="review-shield-message-viewer">
                    <DialogHeader>
                        <DialogTitle>
                            {t('reviewShield.messageViewerTitle', 'Guest Comment')}
                        </DialogTitle>
                        <DialogDescription>
                            {messageViewer?.location_name || messageViewer?.card_friendly_name || ''}
                            {messageViewer?.timestamp ? ` · ${formatDate(messageViewer.timestamp)}` : ''}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-2">
                        <p
                            className="whitespace-pre-wrap break-words text-sm text-foreground"
                            data-testid="review-shield-message-viewer-body"
                        >
                            {messageViewer?.message ? (
                                <HighlightedText text={messageViewer.message} query={effectiveSearch} />
                            ) : null}
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setMessageViewer(null)} data-testid="review-shield-message-viewer-close">
                            {t('common.close', 'Close')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* v4.45 — Email Details modal. Opens on 🟢 Yes click; shows the
                captured email + one-tap actions (Copy, mailto, View Voucher)
                and voucher metadata when applicable. Reuses existing data —
                no new storage. */}
            <Dialog open={!!emailViewer} onOpenChange={(open) => { if (!open) closeEmailViewer(); }}>
                <DialogContent data-testid="review-shield-email-viewer">
                    <DialogHeader>
                        <DialogTitle>
                            {t('reviewShield.emailViewerTitle', 'Email Captured')}
                        </DialogTitle>
                        <DialogDescription>
                            {emailViewer?.location_name || emailViewer?.card_friendly_name || ''}
                            {emailViewer?.timestamp ? ` · ${formatDate(emailViewer.timestamp)}` : ''}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-2 space-y-4">
                        <div>
                            <p
                                className="font-mono text-lg break-all select-all bg-muted rounded-md px-3 py-2"
                                data-testid="review-shield-email-viewer-address"
                            >
                                {emailViewer?.email || emailViewerVoucher?.email || t('reviewShield.emailNotStored', '—')}
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {(emailViewer?.email || emailViewerVoucher?.email) && (
                                <>
                                    <Button
                                        variant={emailCopied ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => copyEmailToClipboard(emailViewer?.email || emailViewerVoucher?.email)}
                                        data-testid="review-shield-email-viewer-copy"
                                    >
                                        {emailCopied
                                            ? `✓ ${t('reviewShield.emailCopied', 'Copied')}`
                                            : `📋 ${t('reviewShield.emailCopy', 'Copy Email')}`}
                                    </Button>
                                    <Button variant="outline" size="sm" asChild data-testid="review-shield-email-viewer-mailto">
                                        <a href={`mailto:${emailViewer?.email || emailViewerVoucher?.email}`}>
                                            ✉️ {t('reviewShield.emailCompose', 'Compose')}
                                        </a>
                                    </Button>
                                </>
                            )}
                        </div>

                        {emailViewerLoading && (
                            <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</p>
                        )}
                        {emailViewerVoucher && (
                            <div className="border rounded-md p-3 space-y-2 bg-muted/40" data-testid="review-shield-email-viewer-voucher">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-semibold uppercase text-muted-foreground">
                                        {t('reviewShield.voucherCol', 'Voucher')}
                                    </span>
                                    <Badge variant={
                                        emailViewerVoucher.status === 'redeemed' ? 'default' :
                                        emailViewerVoucher.status === 'expired' ? 'destructive' :
                                        emailViewerVoucher.status === 'cancelled' ? 'destructive' :
                                        'secondary'
                                    } className="text-xs capitalize">
                                        {t(`reviewShield.voucherStatus.${emailViewerVoucher.status}`, emailViewerVoucher.status)}
                                    </Badge>
                                </div>
                                <a
                                    href={`/voucher-settings?voucher_source_id=${emailViewerVoucher.voucher_source_id || ''}&voucher_code=${emailViewerVoucher.voucher_code}&interaction_id=${emailViewer?.id || ''}`}
                                    className="block font-medium text-emerald-700 dark:text-emerald-400 hover:underline"
                                    data-testid="review-shield-email-viewer-voucher-link"
                                >
                                    🎟 {emailViewerVoucher.voucher_name || t('reviewShield.voucherNameFallback', 'Voucher')}
                                </a>
                                <div className="text-xs text-muted-foreground space-y-0.5">
                                    <div><strong>{t('reviewShield.voucherCode', 'Code')}:</strong> <span className="font-mono">{emailViewerVoucher.voucher_code}</span></div>
                                    {emailViewerVoucher.date_issued && (
                                        <div><strong>{t('reviewShield.dateIssued', 'Date Issued')}:</strong> {formatDate(emailViewerVoucher.date_issued)}</div>
                                    )}
                                    {emailViewerVoucher.date_redeemed && (
                                        <div><strong>{t('reviewShield.dateRedeemed', 'Date Redeemed')}:</strong> {formatDate(emailViewerVoucher.date_redeemed)}</div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={closeEmailViewer} data-testid="review-shield-email-viewer-close">
                            {t('common.close', 'Close')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {deleteTarget ? 'Delete Feedback' : `Delete ${selectedIds.length} Records`}
                        </DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete {deleteTarget ? 'this feedback record' : `${selectedIds.length} selected records`}? This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={confirmDelete}
                            disabled={deleting}
                            data-testid="review-shield-confirm-delete-btn"
                        >
                            {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
                            {t('common.delete', 'Delete')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Cleanup Dialog */}
            <Dialog open={cleanupDialogOpen} onOpenChange={setCleanupDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('reviewShieldResults.cleanupOldRecords')}</DialogTitle>
                        <DialogDescription>
                            Delete feedback records older than the specified number of days.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <label className="text-sm font-medium text-foreground mb-2 block">
                            Delete records older than:
                        </label>
                        <div className="flex items-center gap-2">
                            <Input
                                type="number"
                                min="1"
                                value={cleanupDays}
                                onChange={(e) => setCleanupDays(e.target.value)}
                                className="w-24"
                                data-testid="review-shield-cleanup-days-input"
                            />
                            <span className="text-sm text-muted-foreground">days</span>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCleanupDialogOpen(false)} disabled={deleting}>
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleCleanup}
                            disabled={deleting || !cleanupDays}
                            data-testid="review-shield-confirm-cleanup-btn"
                        >
                            {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
                            Delete Old Records
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Recovery Conversation Panel */}
            <Sheet open={!!activeConversationId} onOpenChange={(open) => { if (!open) handleConversationClose(); }}>
                <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col" data-testid="conversation-sheet">
                    {activeConversationId && (
                        <RecoveryConversationPanel
                            sessionId={activeConversationId}
                            onClose={handleConversationClose}
                            onStatusChange={handleConversationStatusChange}
                        />
                    )}
                </SheetContent>
            </Sheet>
        </div>
    );
};

export default ReviewShieldResults;
